import { useLayoutEffect, useRef } from "react";

/**
 * Reads the element's collapsed height in px — its line-height times the given
 * number of collapsed lines — falling back to 32px (2rem) per line when the
 * computed value is not a usable number.
 */
const getCollapsedHeight = (element, lines = 1) => {
  const lineHeight = Number.parseFloat(getComputedStyle(element).lineHeight);
  return (Number.isFinite(lineHeight) ? lineHeight : 32) * lines;
};

/**
 * Drives a smooth, interruptible max-height animation for a text element that
 * toggles between a single clamped line and its full multi-line height.
 *
 * Instead of animating to a fixed max-height (which feels uneven because most
 * of the time is spent on an invisible empty range) it measures the element's
 * real content height and animates exactly to it. The animation always starts
 * from the element's *current* rendered height, so toggling mid-animation
 * (e.g. closing right after opening) continues seamlessly from wherever it is
 * instead of snapping. The text is kept un-clamped (multi-line) while animating
 * and the single-line ellipsis is only restored once a collapse finishes.
 *
 * The element is expected to default to a clamped single line via CSS
 * (display: -webkit-box; -webkit-line-clamp: 1; max-height: <one line>;
 * overflow: hidden; transition: max-height ...). This hook only overrides those
 * values inline while animating and hands them back to the stylesheet when an
 * expand settles or a collapse finishes.
 *
 * @param {React.RefObject<HTMLElement>} ref element to animate.
 * @param {boolean} isExpanded whether the full text should currently be shown.
 * @param {number} [collapsedLines=1] number of lines the element is clamped to
 *   via CSS (`-webkit-line-clamp`) when collapsed.
 */
const useSmoothExpand = (ref, isExpanded, collapsedLines = 1) => {
  const didMount = useRef(false);

  useLayoutEffect(() => {
    // Skip the first run so the element keeps its CSS collapsed state on mount
    // without playing an animation.
    if (!didMount.current) {
      didMount.current = true;
      return;
    }

    const element = ref.current;
    if (!element) {
      return;
    }

    const collapsedHeight = getCollapsedHeight(element, collapsedLines);

    // Render every line so we can measure the true content height. While
    // collapsed the surrounding max-height + overflow keep it visually at one
    // line, so this does not cause a flash.
    element.style.webkitLineClamp = "unset";

    // scrollHeight is the full content height regardless of the current
    // max-height clip; the bounding rect gives the live (possibly mid-animation)
    // height to start from, which keeps interruptions seamless.
    const fullHeight = element.scrollHeight;
    const currentHeight = element.getBoundingClientRect().height;
    const targetHeight = isExpanded ? fullHeight : collapsedHeight;

    // Pin the starting height, then animate to the target. Reading the layout
    // forces a reflow so the starting height is committed before it changes.
    element.style.maxHeight = `${currentHeight}px`;
    element.getBoundingClientRect();
    element.style.maxHeight = `${targetHeight}px`;

    const onEnd = (event) => {
      if (event.propertyName !== "max-height") {
        return;
      }
      if (isExpanded) {
        // Let the text reflow freely while expanded (e.g. on column resize).
        element.style.maxHeight = "none";
      } else {
        // Hand height + clamp back to the stylesheet (single line + ellipsis).
        element.style.maxHeight = "";
        element.style.webkitLineClamp = "";
      }
    };
    element.addEventListener("transitionend", onEnd);
    return () => element.removeEventListener("transitionend", onEnd);
  }, [isExpanded, ref, collapsedLines]);
};

export default useSmoothExpand;
